import AppApi from "../api/CognitoApi";
import {
  getCurrentUser
} from '@aws-amplify/auth';
import awsconfig from '../aws-exports';
import React, { useState, useRef, useEffect } from 'react';
import { Amplify } from 'aws-amplify';

import ForgotPasswordRequest from "./ForgotPasswordRequest";
import ForgotPasswordConfirm from "./ForgotPasswordConfirm";

Amplify.configure(awsconfig);

function SignIn({ onSignin }) {
  const [isActive, setIsActive] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm_change_password, setConfirmChangePassword] = useState('');
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [state, onStateChange] = useState('login');
  const [confirmPassword, setConfirmPassword] = useState("");
  // Forgot password states
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const signedInRef = useRef(false);
  const Api = new AppApi();

  //-----------------------------------------
  // Check if user already signed in
  //-----------------------------------------
  useEffect(() => {
    Amplify.configure(awsconfig);
  }, []);

  useEffect(() => {
    const checkUserSignedIn = async () => {
      try {
        await getCurrentUser(); // Amplify v6
        return true;
      } catch (error) {
        return false;
      }
    };

    checkUserSignedIn().then(isSignedIn => {
      if (isSignedIn) {
        onSignin();
      }
    });
  }, [onSignin]);

  //-----------------------------------------
  // Handle Sign In / Change Password Flow
  //-----------------------------------------
  const handleSignIn = async (e) => {
    e.preventDefault();
    if (isSigningIn) return;

    if (!username || username.trim() === "") {
      alert('Username is required');
      return;
    }

    if (!password || password.trim() === "") {
      alert('Password is required');
      return;
    }

    setIsSigningIn(true);

    try {
      let user = null;

      if (state === 'login') {
        user = await Api.signIn(username.trim(), password);
      } else {
        if (password !== confirm_change_password) {
          alert('Passwords do not match');
          return;
        }
        user = await Api.ConfirmSignIn(username.trim(), password);
      }

      // Handle new password required
      if (user?.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        alert('Password change is required');
        onStateChange('change_password');
        return;
      }

      signedInRef.current = true;
      onSignin();

    } catch (error) {
      console.error(error);
      alert(error.message || "Sign in failed.");
    } finally {
      setIsSigningIn(false);
    }
  };

  //-----------------------------------------
  // Button animations
  //-----------------------------------------
  const handleClick = () => {
    setIsActive(true);
    setTimeout(() => setIsActive(false), 2000);
  };

  //-----------------------------------------
  // Toggle password visibility
  //-----------------------------------------
  const togglePasswordVisibility = (e) => {
    e.preventDefault();
    setShowPassword(!showPassword);
  };

  //-----------------------------------------
  // Renders the current view
  //-----------------------------------------
  function renderSwitch(param) {
    switch (param) {
      // ====================
      // LOGIN SCREEN
      // ====================
      case 'login':
        return (
          <form onSubmit={handleSignIn} style={formStyle}>
            <h2 style={titleStyle}>Sign In</h2>

            <div style={inputWrapper}>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username"
                style={inputStyle}
              />
            </div>

            <div style={inputWrapper}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                style={inputStyle}
              />
              <button onClick={togglePasswordVisibility} style={showButtonStyle} type="button">
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>

            <button
              type="submit"
              disabled={isSigningIn}
              style={{
                ...submitButton,
                background: isActive
                  ? '#15263f'
                  : 'linear-gradient(135deg, #1f6feb 0%, #0f355e 100%)'
              }}
              onClick={handleClick}
            >
              Sign In
            </button>

            <button
              type="button"
              onClick={() => onStateChange('forgot_password_request')}
              style={forgotButton}
            >
              Forgot Password?
            </button>
          </form>
        );

      // ====================
      // CHANGE PASSWORD (First Login)
      // ====================
      case 'change_password':
        return (
          <form onSubmit={handleSignIn} style={formStyle}>
            <h2 style={titleStyle}>Confirm Password</h2>

            <div style={inputWrapper}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                style={inputStyle}
              />
              <button onClick={togglePasswordVisibility} style={showButtonStyle} type="button">
                {showPassword ? 'Hide' : 'Show'}
              </button>

              <input
                type={showPassword ? 'text' : 'password'}
                value={confirm_change_password}
                onChange={(e) => setConfirmChangePassword(e.target.value)}
                placeholder="Confirm Password"
                style={inputStyle}
              />
            </div>

            <button
              type="submit"
              disabled={isSigningIn}
              style={{
                ...submitButton,
                background: isActive
                  ? '#15263f'
                  : 'linear-gradient(135deg, #1f6feb 0%, #0f355e 100%)'
              }}
              onClick={handleClick}
            >
              Confirm
            </button>
          </form>
        );

      // ====================
      // FORGOT PASSWORD REQUEST
      // ====================
      case 'forgot_password_request':
        return (
          <ForgotPasswordRequest
            username={username}
            setUsername={setUsername}
            Api={Api}
            onStateChange={onStateChange}
          />
        );

      // ====================
      // FORGOT PASSWORD CONFIRMATION
      // ====================
      case 'forgot_password_confirm':
        return (
          <ForgotPasswordConfirm
            username={username}
            resetCode={resetCode}
            setResetCode={setResetCode}
            newPassword={newPassword}
            setNewPassword={setNewPassword}
            confirmPassword={confirmPassword}
            setConfirmPassword={setConfirmPassword}
            showPassword={showPassword}
            Api={Api}
            onStateChange={onStateChange}
          />
        );

      default:
        return <div></div>;
    }
  }

  //-----------------------------------------
  // Render outer container
  //-----------------------------------------
  return (
    <div style={containerStyle}>
      {renderSwitch(state)}
    </div>
  );
}

//-----------------------------------------
// Inline Styles (unchanged, just grouped)
//-----------------------------------------
const containerStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  background: 'linear-gradient(180deg, #0f1e33 0%, #0b1422 100%)',
  userSelect: 'none'
};

const formStyle = {
  width: '300px',
  padding: '40px',
  backgroundColor: '#111d30',
  borderRadius: '12px',
  border: '1px solid #22416b',
  boxShadow: '0px 16px 36px rgba(10, 22, 42, 0.45)'
};

const titleStyle = {
  textAlign: 'center',
  fontSize: '24px',
  marginBottom: '20px',
  color: '#e7edf7'
};

const inputWrapper = {
  marginBottom: '20px'
};

const inputStyle = {
  width: '100%',
  padding: '10px',
  fontSize: '16px',
  border: '1px solid #22416b',
  borderRadius: '8px',
  marginBottom: '10px',
  backgroundColor: '#0f1e33',
  color: '#e7edf7'
};

const showButtonStyle = {
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: '#5dd0ff'
};

const submitButton = {
  width: '100%',
  padding: '10px',
  fontSize: '16px',
  color: '#e7edf7',
  border: '1px solid #2c4f83',
  borderRadius: '10px',
  cursor: 'pointer',
  background: 'linear-gradient(135deg, #1f6feb 0%, #0f355e 100%)',
  boxShadow: '0 10px 24px rgba(15, 53, 94, 0.45)'
};

const forgotButton = {
  marginTop: '10px',
  width: '100%',
  padding: '10px',
  fontSize: '14px',
  backgroundColor: 'transparent',
  border: 'none',
  color: '#5dd0ff',
  cursor: 'pointer',
  textDecoration: 'underline'
};

export default SignIn;
